import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Campaign} from '../../../../models/campaign';
import {CampaignService} from '../../../services/campaign/campaign.service';
import {NumberUtils} from '../../../utils/number.utils';
import {CampaignInfluencerStatus} from '../../../app.constants';
import {UserService} from '../../../services/user.service';
import {
  ActionSource,
  ActivePages,
  EventTrackerService,
  Page,
  TargetType,
  UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {CacheService} from '../../../services/cache/cache.service';
import {Router} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {DeviceDetectorService} from 'ngx-device-detector';
import { AppSettings } from '../../../utils/app-settings';

@Component({
  selector: 'campaign-item',
  templateUrl: './campaign-item.component.html',
  styleUrls: ['./campaign-item.component.scss']
})
export class CampaignItemComponent implements OnInit {

  @Input()
  public campaign: Campaign;
  @Input() enableInfluencerPlatform: boolean;
  @Input() isCampaignHistory: boolean;

  @Output()
  public eventSelectCampaign = new EventEmitter<number>();
  @Output()
  public eventDeleteCampaign = new EventEmitter<number>();
  @Output()
  public eventReloadSelectedCampaign = new EventEmitter<any>();
  @Output()
  public eventOpenAddInfluencer = new EventEmitter<any>();
  @Output()
  public eventOpenMeasurePage = new EventEmitter<any>();
  @Output()
  goToCampaignDetails = new EventEmitter<any>();

  constructor(private campaignService: CampaignService,
              private deviceService: DeviceDetectorService,
              private userService: UserService,
              private cacheService: CacheService,
              private route: Router,
              private eventTracker: EventTrackerService,
              private translate: TranslateService) {}

  ngOnInit() {
  }

  private fetchInfluencerByCampaign(campaign, toggle: Boolean = true) {
    const campaignId = campaign.id;
    this.cacheService.get(`campaign_influencers_${campaignId}`,
      this.campaignService.getInfluencerByCampaign(campaignId)).subscribe((res: any) => {
      this.campaignService.updateCampaignDetailsData(this.campaign, res);
    });
  }

  selectCampaign(campaign) {
    if (this.deviceService.isMobile()) {
      this.openCampaignDetails(campaign.id, campaign.campaignInfluencerId, true);
    } else {
      this.expandCampaignInfluencerList(campaign);
    }
  }

  private expandCampaignInfluencerList(campaign) {
    const isExpaned = campaign.is_expanded;
    this.eventSelectCampaign.emit(campaign);
    // Report event to Segment/MP
    this.campaign.is_expanded = !isExpaned;
    if (!isExpaned) {
      // segment track event
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CAMPAIGN, {
        id: campaign.id,
        name: campaign.name,
        type: TargetType.CAMPAIGN_DETAILS
      }, {
        action: UserActionType.TOGGLE,
        source: ActionSource.PAGE,
      }, undefined, {
        type: this.isCampaignHistory ? ActivePages.CAMPAIGN_HISTORY : ActivePages.CAMPAIGN_OVERVIEW
      });
    }
  }

  deleteCampaign(campaign) {
    this.eventDeleteCampaign.emit(campaign);
  }

  reloadCampaign() {
    this.cacheService.clear(`campaign_influencers_${this.campaign.id}`);
    this.fetchInfluencerByCampaign(this.campaign, false);
  }

  normalizeNumberData(value: number): string {
    if (!value || (this.enableInfluencerPlatform && this.isNewCampaign(this.campaign))) {
      return '0';
    }
    return NumberUtils.normalizeNumberToString(value);
  }

  getInfluencerPostCount(campaign) {
    if (this.enableInfluencerPlatform && this.isNewCampaign(this.campaign)) {
      return 0;
    }
    return campaign.numberOfPosts;
  }

  getNewPublishedPostCount(campaign) {
    if (this.enableInfluencerPlatform && this.isNewCampaign(this.campaign)) {
      return null;
    }
    return campaign.newPosts;
  }

  openAddInfluencer(event) {
    this.selectCampaign(this.campaign);
    this.eventOpenAddInfluencer.emit(this.campaign);
  }

  openMeasurePage(event) {
    this.eventOpenMeasurePage.emit(this.campaign);
  }

  openCampaignDetails(campaignId, campaignInfluencerId, onMobile?) {
    this.cacheService.disableCacheRemoving();
    // if current user is influencer, should open specific campaign details page for influencer
    const statusActive = [CampaignInfluencerStatus.ACTIVE.toLocaleLowerCase()];
    if (this.enableInfluencerPlatform) {
      if (statusActive.indexOf(this.campaign.status.toLocaleLowerCase()) >= 0 && !onMobile) {
        return this.route.navigate(['dashboard', 'campaigns', campaignInfluencerId, 'active']);
      } else {
        if (this.isNewCampaign(this.campaign)) {
          return this.route.navigate(['dashboard', 'campaigns', campaignId, 'new'], {
            queryParams: {
              isNewCampaign: true
            }
          });
        } else {
          return this.route.navigate(['dashboard', 'campaigns', campaignId, 'new']);
        }
      }
    } else if (this.isCampaignHistory) {
      return this.route.navigate(['campaigns', campaignId], {
        queryParams: {
          activeAccount: this.activeAccount,
          isCampaignHistory: this.isCampaignHistory
        }
      });
    } else {
      // otherwise open the default campaign page
      return this.route.navigate(['campaigns', campaignId], {
        queryParams: {
          activeAccount: this.activeAccount
        }
      });
    }
  }

  getCampaignDetailButtonColorClass(campaign) {
    const status = (campaign.status + '').toLowerCase();
    // Todo: I don't know actual statuses and logic to get status here
    // Should correctly implement mapping status to button color
    const colors = {
      'invited': 'blue',
      'new': 'blue',
      'active': 'green',
      'ended': 'silver',
      'rejected': 'red'
    };
    if (campaign.campaignType === CampaignInfluencerStatus.ARCHIVED) {
      return 'silver';
    }
    return colors[status] || 'silver';
  }

  get activeAccount() {
    return this.userService.getCompanyId();
  }

  get influencerList() {
    if (!this.campaign || !Array.isArray(this.campaign.influencers) || this.campaign.influencers.length === 0) {
      return [];
    }
    if (AppSettings.isCampaignInfluencerDetails()) {
      this.campaign.influencers = this.campaign.influencers.sort((a, b) => a.campaign_influencer_reach.value < b.campaign_influencer_reach.value ? 1 : a.campaign_influencer_reach.value > b.campaign_influencer_reach.value ? -1 : 0);
      return this.campaign.influencers.slice(0, 3);
    }
    return this.campaign.influencers;
  }

  get postList() {
    if (!this.campaign || this.isNewCampaign(this.campaign) || !Array.isArray(this.campaign.posts)) {
      return [];
    }
    return this.campaign.posts;
  }

  get secondColData() {
    if (this.enableInfluencerPlatform && this.campaign.campaignType !== CampaignInfluencerStatus.ARCHIVED) {
      return this.campaign.status;
    } else if (this.enableInfluencerPlatform && this.campaign.campaignType === CampaignInfluencerStatus.ARCHIVED) {
      return CampaignInfluencerStatus.DELETE;
    } else {
      return this.campaign.numberOfInfluencers;
    }
  }

  get canDeleteCampaign() {
    if (!this.enableInfluencerPlatform) {
      return true;
    }
    return this.campaign && this.campaign.status === CampaignInfluencerStatus.NEW;
  }

  isNewCampaign(campaign: Campaign) {
    // only apply for influencer platform
    if (this.enableInfluencerPlatform) {
      return campaign && campaign.status === CampaignInfluencerStatus.NEW;
    } else {
      return false;
    }
  }

  get buttonLabel() {
    return this.campaign.campaignType && this.campaign.campaignType === CampaignInfluencerStatus.ARCHIVED ? CampaignInfluencerStatus.DELETE : this.translate.instant('common.btn.details');
  }

  get isCampaignInfluencerDetails() {
    return AppSettings.isCampaignInfluencerDetails();
  }

  public requestGoToCampaignDetails(): void {
    this.goToCampaignDetails.emit();
  }
}



// WEBPACK FOOTER //
// ./src/app/main/campaigns/campaign-item/campaign-item.component.ts